import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {UrlBuilder} from "../item/url.item";
import {WrapperDto} from "../dto/wrapper.dto";
import {map} from "rxjs/operators";
import {OrgDto} from "../dto/org.dto";
import {Additionals} from "../item/additionals";
import {SettingDto} from "../dto/setting.dto";
import {VoidDto} from "../dto/void.dto";
import {AddressDto} from "../dto/address.dto";
import {PhoneDto} from "../dto/phone.dto";
import {BankAddressDto} from "../dto/bank-address.dto";
import {Observable} from "rxjs";
import {StorageService} from "../../services/storage/storage.service";

@Injectable({providedIn: 'root'})
export class OrgRepo {

  private readonly orgRead = '/v1/org/{id}';
  private readonly orgWrite = '/v1/org/{id}';
  private readonly orgPing = '/v1/org/{id}/ping';
  private readonly settingsWrite = '/v1/org/{id}/settings';
  private readonly orgLogoUpload = '/v1/org/{id}/logo';
  private readonly orgLogoDelete = '/v1/org/{id}/logo/delete';
  private readonly addressAllRead = '/v1/org/{id}/address';
  private readonly addressWrite = '/v1/org/{id}/address';
  private readonly addressDelete = '/v1/org/{id}/address/{addressId}/delete';
  private readonly phoneAllRead = '/v1/org/{id}/phone';
  private readonly phoneWrite = '/v1/org/{id}/phone';
  private readonly phoneDelete = '/v1/org/{id}/phone/{phoneId}/delete';
  private readonly bankAddressAllRead = '/v1/org/{id}/bankaddress';
  private readonly bankAddressWrite = '/v1/org/{id}/bankaddress';
  private readonly bankAddressDelete = '/v1/org/{id}/bankaddress/{bankaddressId}/delete';

  private readonly http: HttpClient = inject(HttpClient);
  private readonly storage: StorageService = inject(StorageService);

  private readonly bankAddressStoragePrefix: string = 'org:bankaddress:';
  private readonly bankAddressStorageExpireInSec: number = 600; // 600 = 10 minutes

  public readById(id: string, add?: Additionals): Observable<WrapperDto<OrgDto>> {
    const url = UrlBuilder.builder().addPath(this.orgRead).addParam('id', id).addAdditionals(add).build();
    return this.http.get<WrapperDto<OrgDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto): Observable<WrapperDto<OrgDto>> {
    const url = UrlBuilder.builder().addPath(this.orgWrite).addParam('id', org.id).build();
    return this.http.post<WrapperDto<OrgDto>>(url.getUrl(), org, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Logo
  // ##########################################################################

  public uploadLogo(org: OrgDto, logoFile: File): Observable<WrapperDto<VoidDto>> {
    const data = new FormData();
    data.append('media', logoFile);
    const url = UrlBuilder.builder().addPath(this.orgLogoUpload).addParam('id', org.id)
      .setContentTypeMultipartFormData().build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), data, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteLogo(org: OrgDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.orgLogoDelete).addParam('id', org.id).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Ping
  // ##########################################################################

  public ping(id: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.orgPing).addParam('id', id).build();
    return this.http.get<WrapperDto<VoidDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Settings
  // ##########################################################################

  public updateSettings(id: string, settings: SettingDto[]): Observable<WrapperDto<SettingDto>> {
    const url = UrlBuilder.builder().addPath(this.settingsWrite).addParam('id', id).build();
    return this.http.post<WrapperDto<SettingDto>>(url.getUrl(), settings, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Addresses
  // ##########################################################################

  public readAddresses(org: OrgDto): Observable<WrapperDto<AddressDto>> {
    const url = UrlBuilder.builder().addPath(this.addressAllRead).addParam('id', org.id).build();
    return this.http.get<WrapperDto<AddressDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateAddress(org: OrgDto, address: AddressDto): Observable<WrapperDto<AddressDto>> {
    const url = UrlBuilder.builder().addPath(this.addressWrite).addParam('id', org.id).build();
    return this.http.post<WrapperDto<AddressDto>>(url.getUrl(), address, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteAddress(org: OrgDto, addressId: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.addressDelete)
      .addParam('id', org.id).addParam('addressId', addressId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Phones
  // ##########################################################################

  public readPhones(org: OrgDto): Observable<WrapperDto<PhoneDto>> {
    const url = UrlBuilder.builder().addPath(this.phoneAllRead).addParam('id', org.id).build();
    return this.http.get<WrapperDto<PhoneDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updatePhone(org: OrgDto, phone: PhoneDto): Observable<WrapperDto<PhoneDto>> {
    const url = UrlBuilder.builder().addPath(this.phoneWrite).addParam('id', org.id).build();
    return this.http.post<WrapperDto<PhoneDto>>(url.getUrl(), phone, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deletePhone(org: OrgDto, phoneId: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.phoneDelete)
      .addParam('id', org.id).addParam('phoneId', phoneId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Bank addresses
  // ##########################################################################

  public readBankAddresses(org: OrgDto): Observable<WrapperDto<BankAddressDto>> {
    return this.storage.cache(this.buildBankAddressCacheKey(org), () => {
      const url = UrlBuilder.builder().addPath(this.bankAddressAllRead).addParam('id', org.id).build();
      return this.http.get<WrapperDto<BankAddressDto>>(url.getUrl(), url.getOptions());
    }, this.bankAddressStorageExpireInSec)
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateBankAddress(org: OrgDto, bankAddress: BankAddressDto): Observable<WrapperDto<BankAddressDto>> {
    this.clearBankAddressesCache(org);
    const url = UrlBuilder.builder().addPath(this.bankAddressWrite).addParam('id', org.id).build();
    return this.http.post<WrapperDto<BankAddressDto>>(url.getUrl(), bankAddress, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteBankAddress(org: OrgDto, bankaddressId: string): Observable<WrapperDto<VoidDto>> {
    this.clearBankAddressesCache(org);
    const url = UrlBuilder.builder().addPath(this.bankAddressDelete)
      .addParam('id', org.id).addParam('bankaddressId', bankaddressId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  private clearBankAddressesCache(org: OrgDto): void {
    this.storage.cacheRemove(this.buildBankAddressCacheKey(org));
  }

  private buildBankAddressCacheKey(org: OrgDto): string {
    return this.bankAddressStoragePrefix + org.id;
  }
}
